/**
 * Browser storage persistence. Without it, the browser may clear this site's IndexedDB
 * on its own (under storage pressure in Chromium and Firefox; after about seven days
 * without a visit in Safari). Asking costs nothing, and the browser decides; some grant it
 * silently, some only for installed or frequently used sites.
 */
export type PersistenceStatus = 'persistent' | 'best-effort' | 'unknown';

const storage = () => (globalThis.navigator as Navigator | undefined)?.storage as StorageManager | undefined;

export async function persistenceStatus(): Promise<PersistenceStatus> {
  try {
    const s = storage();
    if (!s?.persisted) return 'unknown';
    return (await s.persisted()) ? 'persistent' : 'best-effort';
  } catch {
    return 'unknown';
  }
}

/** Asks the browser to keep this site's data. Never throws; resolves to the resulting status. */
export async function requestPersistentStorage(): Promise<PersistenceStatus> {
  try {
    const s = storage();
    if (!s?.persist) return 'unknown';
    return (await s.persist()) ? 'persistent' : 'best-effort';
  } catch {
    return 'unknown';
  }
}
